import { Avatar, Badge, Box, Button, Collapse, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { MessagingSectionHeader } from './MessagingSectionHeader';

export type Participant = {
  id: number | string;
  name?: string;
  avatarUrl?: string;
  CohortUser: { id: number; role: string };
};

type MessagingSectionProps = {
  iconSource: string;
  title: string;
  participants: Participant[];
  dmCounts: Record<string, number>;
  onRowClick: (participantId: number | string) => void;
  accountabilityPartners?: number[]; // list of CohortUser.id that are APs
  defaultOpen?: boolean;
};

export const MessagingSection: React.FC<MessagingSectionProps> = ({
  iconSource,
  title,
  participants,
  dmCounts,
  onRowClick,
  accountabilityPartners = [],
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const totalUnread = participants.reduce((sum, p) => sum + (dmCounts[p.id.toString()] || 0), 0);

  const getBadgeImageUrl = (p: Participant, isAP: boolean): string | null => {
    const roleLower = p.CohortUser.role?.toLowerCase?.() || '';
    if (isAP) return '/assets/partner-badge.png';
    if (roleLower === 'coach') return '/assets/coach-badge.png';
    if (roleLower === 'captain') return '/assets/captain-badge.png';
    return null;
  };

  const getRoleLabel = (p: Participant, isAP: boolean): string => {
    const roleLower = p.CohortUser.role?.toLowerCase?.() || '';
    if (isAP) return 'Accountability Partner';
    if (roleLower === 'coach') return 'Coach';
    if (roleLower === 'captain') return 'Captain';
    return 'Hater';
  };

  return (
    <>
      <MessagingSectionHeader
        iconSource={iconSource}
        title={title}
        count={totalUnread}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        participants={participants}
        accountabilityPartners={accountabilityPartners}
      />

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Grid container spacing={'12px'} sx={{ mt: '15px' }}>
          {participants.map((participant) => {
            if (!participant?.name) return null;

            const isAP = accountabilityPartners.includes(participant.CohortUser.id);
            const badgeImageUrl = getBadgeImageUrl(participant, isAP);
            const roleLabel = getRoleLabel(participant, isAP);

            return (
              <Grid size={{ xs: 12 }} key={participant.id}>
                <Button
                  onClick={() => onRowClick(participant.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textTransform: 'none',
                    p: 1,
                  }}
                >
                  {/* Left: Avatar + unread + small role badge + name/role */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                      badgeContent={dmCounts[participant.id.toString()] || 0}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      sx={{
                        overflow: 'visible',
                        '& .MuiBadge-badge': {
                          backgroundColor: '#4DBB21',
                          color: 'white',
                          width: '26px',
                          height: '26px',
                          borderRadius: '100px',
                          left: '10px',
                          bottom: '5px',
                          boxShadow: '0px 4px 4px 0px #0000001F',
                          fontWeight: 500,
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          alt={participant.name}
                          src={participant.avatarUrl}
                          sx={{ width: 48, height: 48 }}
                          slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                        />
                        {badgeImageUrl && (
                          <img
                            src={badgeImageUrl}
                            alt={`${roleLabel} Badge`}
                            style={{ position: 'absolute', bottom: 0, right: 0, width: '48px' }}
                          />
                        )}
                      </Box>
                    </Badge>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
                      <Typography sx={{ fontWeight: 'bold' }}>{participant.name}</Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#7A7A7A' }}>{roleLabel}</Typography>
                    </Box>
                  </Box>

                  {/* Right: image */}
                  <img src="/assets/svg/paper-plane-icon.svg" alt="Message" style={{ width: 21, height: 21 }} />
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Collapse>
    </>
  );
};

export default MessagingSection;
